import { ApolloLink, FetchResult, Observable, Operation } from "@apollo/client";
import { print } from "graphql";
import { Client, ClientOptions, createClient } from "graphql-sse";

/** First delay between transport reconnect attempts. */
export const SSE_RETRY_BASE_DELAY_MS = 1000;
/** Ceiling for the reconnect backoff, so a long outage still retries steadily. */
export const SSE_RETRY_MAX_DELAY_MS = 30_000;
/**
 * A stream that ends sooner than this is treated as unhealthy, so resubscribing
 * backs off instead of hot-looping. The server's bounded lifetime is minutes.
 */
const MIN_HEALTHY_STREAM_MS = 1000;

/**
 * Capped exponential backoff with full jitter. The jitter matters on the deploy
 * case this policy exists for: every client drops at the same instant, so a
 * deterministic delay would reconnect them all in lockstep.
 */
export function sseRetryDelayMs(
  retries: number,
  random: () => number = Math.random
): number {
  const capped = Math.min(
    SSE_RETRY_BASE_DELAY_MS * 2 ** retries,
    SSE_RETRY_MAX_DELAY_MS
  );
  return Math.round(capped * random());
}

/**
 * Apollo link that carries GraphQL subscriptions over Server-Sent Events using
 * the `graphql-sse` client. Queries and mutations never reach this link — an
 * Apollo `split()` routes only `subscription` operations here; everything else
 * stays on the existing HTTP link chain.
 *
 * Auth reuses the same Keycloak token as queries: pass a `headers` function in
 * `ClientOptions` that returns a fresh `Authorization` header per (re)connect.
 */
export class SSELink extends ApolloLink {
  private client: Client;
  private disposed = false;

  constructor(options: ClientOptions) {
    super();
    this.client = createClient({
      // `graphql-sse` defaults to five attempts and then reports a terminal
      // error, which would leave subscriptions dead for the rest of the page's
      // life after any outage longer than a few seconds. Retry indefinitely
      // instead; `dispose()` aborts the connection, and the client re-checks
      // the abort signal after each retry delay, so this cannot outlive the link.
      retryAttempts: Infinity,
      retry: retries =>
        new Promise(resolve => setTimeout(resolve, sseRetryDelayMs(retries))),
      ...options,
    });
  }

  public request(operation: Operation): Observable<FetchResult> {
    // Send only the protocol fields. Spreading the whole `operation` would leak
    // its `extensions` (defaults to `{}`) into the request body, and the backend
    // treats any truthy `extensions` as a persisted-operation request and
    // rejects it with "missing graphql persisted-query hash".
    const payload = {
      operationName: operation.operationName,
      query: print(operation.query),
      variables: operation.variables,
    };

    return new Observable(sink => {
      let unsubscribed = false;
      let disposeCurrent: (() => void) | undefined;
      let resubscribeTimer: ReturnType<typeof setTimeout> | undefined;
      let unhealthyStreams = 0;

      const open = (): void => {
        // Guarded here, not only where a resubscribe is scheduled: the backoff
        // timer captures the decision to reopen but runs later, and `dispose()`
        // (identity change → the provider rebuilds the Apollo client) does not
        // tear down this Observable, so nothing clears that timer the way the
        // unsubscribe path does. Without this it would subscribe on a disposed
        // client, whose aborted connection surfaces as a spurious error on a
        // stream the consumer has already moved on from.
        if (unsubscribed || this.disposed) return;
        const startedAt = Date.now();
        // A failed operation is NOT a transport event: graphql-sse delivers
        // execution results — errors included — through `next` and then calls
        // `complete`, so a denied document looks exactly like a lifetime
        // rollover from out here. Resubscribing on those would retry a
        // permanent failure forever. A partial result (data *and* errors) is a
        // live stream reporting a per-emit problem and stays retryable.
        //
        // `data: null` alongside errors is not enough on its own to call it
        // permanent. A subscription's root field is typically non-null, so a
        // resolver throwing *mid-stream* — a transient read failure behind a
        // live, authorized subscription — nullifies `data` and arrives in
        // exactly the same shape as a subscribe-time denial. Server-side
        // masking collapses both to "something went wrong" in prod, so there is
        // no error code to tell them apart either.
        //
        // What does tell them apart is whether this stream ever worked: a setup
        // or authorization failure fails before the first payload, a runtime
        // one only after. Tracked per stream, not per subscription — a document
        // whose access is revoked between two streams must still be able to end
        // the subscription, and it does on the next one, which delivers nothing.
        //
        // Both flags are monotonic and the verdict is computed where it is read,
        // so ordering cannot matter: a payload arriving *after* an error-only
        // result still clears the verdict. That case is real — graphql-js maps
        // each source value through `execute`, which *returns* field errors
        // rather than throwing, so the source keeps producing; an error raised
        // per payload (the `@authenticated` directive wraps `resolve`, which
        // runs per emit) can be followed by good data on the same stream, and
        // that later payload is proof this was never a setup failure.
        let sawPayload = false;
        let sawErrorOnlyResult = false;
        disposeCurrent = this.client.subscribe<
          Record<string, unknown>,
          Record<string, unknown>
        >(payload, {
          next: result => {
            if (result.data != null) {
              sawPayload = true;
            } else if (result.errors?.length) {
              sawErrorOnlyResult = true;
            }
            // Rebuilt rather than forwarded as-is: `FetchResult` is a union
            // whose incremental-delivery member requires `data`, so an
            // optional-`data` execution result only satisfies the single-result
            // member once `data` is explicitly present.
            sink.next({
              data: result.data ?? null,
              errors: result.errors,
              extensions: result.extensions,
            });
          },
          // Otherwise a server-sent `complete` is not the end of this
          // subscription. The backend closes every stream at its bounded
          // lifetime to force re-authentication with a fresh token, and
          // `graphql-sse` only reconnects on a `NetworkError` — a normal
          // completion exits its loop for good. Without resubscribing here,
          // every subscription would die at the first lifetime boundary.
          complete: () => {
            if (unsubscribed || this.disposed) return;
            if (sawErrorOnlyResult && !sawPayload) {
              sink.complete();
              return;
            }
            unhealthyStreams =
              Date.now() - startedAt < MIN_HEALTHY_STREAM_MS
                ? unhealthyStreams + 1
                : 0;
            if (unhealthyStreams === 0) {
              open();
              return;
            }
            resubscribeTimer = setTimeout(
              open,
              sseRetryDelayMs(unhealthyStreams - 1)
            );
          },
          error: sink.error.bind(sink),
        });
      };

      open();

      return () => {
        unsubscribed = true;
        if (resubscribeTimer) clearTimeout(resubscribeTimer);
        disposeCurrent?.();
      };
    });
  }

  /**
   * Terminate the underlying `graphql-sse` client, closing every open SSE
   * connection. The Apollo client is recreated whenever auth/selected identity
   * changes; without this the abandoned client's streams keep running under the
   * stale identity until the server's max-stream deadline.
   */
  public dispose(): void {
    this.disposed = true;
    this.client.dispose();
  }
}
